import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import apiClient from '../../services/api';
import { Mail, Search, Download, Users, ShoppingBag, AtSign } from 'lucide-react';

const Marketing = () => {
  const [data, setData] = useState({ subscribers: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await apiClient.get('/admin/subscribers');
      setData(res.data);
    } catch (e) {
      console.error('Subscribers load failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const rows = [['Nom', 'Email', 'Téléphone', 'Commandes', 'Total dépensé (DA)', 'Dernière commande']];
    data.subscribers.forEach((s) => {
      rows.push([
        s.name || '',
        s.email || '',
        s.phone || '',
        s.orders_count || 0,
        Math.round(s.total_spent || 0),
        s.last_order_at ? new Date(s.last_order_at).toLocaleDateString('fr-FR') : '',
      ]);
    });
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gcstore_clients_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = data.subscribers.filter((s) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.phone || '').toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Mail className="w-7 h-7 text-cyan-400" />
              Marketing & Clients
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Liste des clients ayant fourni leur email — exportable pour vos campagnes promo
            </p>
          </div>
          <button
            onClick={exportCsv}
            disabled={!data.subscribers.length}
            className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-purple-500 text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="export-csv-btn"
          >
            <Download className="w-5 h-5" />
            Exporter CSV
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-cyan-500" />
              <span className="text-gray-500 text-sm">Abonnés (avec email)</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {data.stats.total_subscribers || 0}
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <ShoppingBag className="w-5 h-5 text-purple-500" />
              <span className="text-gray-500 text-sm">Total commandes</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {data.stats.total_orders || 0}
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <AtSign className="w-5 h-5 text-green-500" />
              <span className="text-gray-500 text-sm">Taux d'opt-in email</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {data.stats.total_orders
                ? Math.round((data.stats.orders_with_email / data.stats.total_orders) * 100)
                : 0}
              %
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Rechercher un client (nom, email, téléphone)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-lg pl-12 pr-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition"
            data-testid="subscribers-search"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Aucun client enregistré</h3>
            <p className="text-gray-500">
              Les clients qui fournissent leur email à la commande apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-gray-700 text-sm font-semibold">Client</th>
                    <th className="text-left px-6 py-3 text-gray-700 text-sm font-semibold">Email</th>
                    <th className="text-left px-6 py-3 text-gray-700 text-sm font-semibold">Téléphone</th>
                    <th className="text-right px-6 py-3 text-gray-700 text-sm font-semibold">Commandes</th>
                    <th className="text-right px-6 py-3 text-gray-700 text-sm font-semibold">Total dépensé</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((s) => (
                    <tr key={s.email} className="hover:bg-gray-50 transition" data-testid={`subscriber-row-${s.email}`}>
                      <td className="px-6 py-3 text-gray-900 font-medium">{s.name}</td>
                      <td className="px-6 py-3">
                        <a
                          href={`mailto:${s.email}`}
                          className="text-cyan-600 hover:text-purple-600 transition"
                        >
                          {s.email}
                        </a>
                      </td>
                      <td className="px-6 py-3 text-gray-700">{s.phone}</td>
                      <td className="px-6 py-3 text-right">
                        <span className="inline-block bg-cyan-100 text-cyan-700 text-xs font-bold px-2 py-1 rounded-full">
                          {s.orders_count}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right font-bold text-gray-900">
                        {Math.round(s.total_spent || 0).toLocaleString('fr-DZ')} DA
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info card for future Resend integration */}
        <div className="mt-8 bg-gradient-to-r from-cyan-50 to-purple-50 border border-cyan-200 rounded-xl p-6">
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            📬 Envoyer une campagne email
          </h3>
          <p className="text-gray-700 text-sm">
            L'envoi de campagnes promo par email sera disponible dès que vous activerez votre clé Resend.
            En attendant, vous pouvez <strong>exporter la liste en CSV</strong> et l'utiliser avec
            n'importe quel outil (Mailchimp, Brevo, Sendinblue, Gmail).
          </p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Marketing;
